//* ===== אילו תנאי תצוגה מותאמת ניתנים להערכה בצד הלקוח =====
//* המעריך החלקי (doesUserMatchActiveCustomViewGroups ב-utils/chat.tsx) יודע להכריע
//* רק על סטטוס פלואו / משויך-ל / תגית שיחה; כל תנאי אחר "עובר" מתוך שמרנות.
//* חקירת 4.11: השמרנות הזו הכניסה לרשימת התצוגה המותאמת כל שיחה שקיבלה הודעה
//* בחברה כשהתצוגה מכילה תנאי לא-נתמך (למשל תגית איש קשר או תאריך) — הרשימה גדלה
//* והתג (שמחושב בשרת) לא. לכן הקוד החי צריך לדעת מראש אם התצוגה "סגורה" להערכה
//* מקומית; אם לא — לא מכניסים שורות חדשות מהסוקט אלא מושכים את העמוד הראשון מחדש.
//* קובץ טהור (בלי store) כדי שאפשר יהיה לבדוק אותו ב-vitest.

export type ClientEvalCondition = {
  field?: string;
  operator?: string;
  value?: unknown;
};

export type ClientEvalFilterGroup = {
  logic?: 'or' | 'and';
  conditions?: ClientEvalCondition[];
};

const SCALAR_FIELDS: ReadonlySet<string> = new Set([
  'lastSession.flowStatusCode',
  'managerId',
  'lastSession.manager.id',
]);
const SCALAR_OPERATORS: ReadonlySet<string> = new Set(['eq', 'in', 'ne', 'notin', 'isnull', 'isnotnull']);
const TAG_VALUE_OPERATORS: ReadonlySet<string> = new Set(['eq', 'in', 'contains', 'ne', 'notin']);
const TAG_NULL_OPERATORS: ReadonlySet<string> = new Set(['isnull', 'isnotnull']);

function hasNonEmptyValue(value: unknown): boolean {
  if (Array.isArray(value)) return value.some((v) => v != null && String(v).length > 0);
  return value != null && String(value).length > 0;
}

/**
 * האם evalSupportedCustomCondition מכריע על התנאי (true/false) ולא מחזיר null.
 * חייב להישאר תואם לטבלת השדות/אופרטורים שם — כל הרחבה של המעריך מתעדכנת גם כאן.
 */
export function isCustomViewConditionClientEvaluable(cond: ClientEvalCondition): boolean {
  const op = cond.operator ?? '';
  if (SCALAR_FIELDS.has(cond.field ?? '')) return SCALAR_OPERATORS.has(op);
  if (cond.field === 'conversationTag') {
    if (TAG_NULL_OPERATORS.has(op)) return true;
    return TAG_VALUE_OPERATORS.has(op) && hasNonEmptyValue(cond.value);
  }
  return false;
}

/**
 * true כשיש בתצוגה לפחות תנאי אחד שהלקוח לא יודע להעריך — ואז הכנסת שורה חדשה
 * מהסוקט לרשימת התצוגה היא ניחוש. קבוצות ריקות לא נספרות; מבנה שאינו FilterGroup
 * (תצוגה legacy שטרם הוסבה) נחשב לא-נתמך.
 */
export function customViewFiltersHaveUnsupportedConditions(filters: unknown): boolean {
  if (!Array.isArray(filters) || filters.length === 0) return false;
  for (const group of filters as unknown[]) {
    if (!group || typeof group !== 'object' || !Array.isArray((group as ClientEvalFilterGroup).conditions)) {
      return true;
    }
    for (const cond of (group as ClientEvalFilterGroup).conditions ?? []) {
      if (!cond || typeof cond !== 'object') return true;
      if (!isCustomViewConditionClientEvaluable(cond)) return true;
    }
  }
  return false;
}
